import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import Card from 'antd/es/card';
import Typography from 'antd/es/typography';
import Avatar from 'antd/es/avatar';
import Tooltip from 'antd/es/tooltip';

import { Space } from 'components/layout';
import InfoLabel from 'components/InfoLabel';
import ProcessBar from 'components/process/ProcessBar';
import Icon from 'components/Icon';

import { dateTimeFormat } from 'config/common';
import doorState from 'config/doorState';
import unloadSpeed from 'config/unloadSpeed';

import { getInitials } from 'helpers/string';
import { formatDate, formatDuration } from 'helpers/date';


// import StatusTag from './StatusTag';

const { Text } = Typography;

const DoorCardBase = styled(Card)`
  background: #FFFFFF;
  border: none;
  border-radius: 8px;

  & .ant-card-body {
    padding: 0;
    margin: 0;
    width: 100%;
    min-height: 100%;
  }

  &:hover {
    cursor: pointer;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const DoorCardContent = styled.div`
  display: flex;
  min-height: 80px;
  align-items: stretch;
`;
const IconsContainer=styled.div`
display:flex;
gap:5px
`;

const InfoBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  justify-content: flex-start;
  min-height: 132px;

  & .ant-typography.status {
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    color: #45494D;
  }

  & .ant-typography {
    font-size: 12px;
    line-height: 20px;
    color: #45494D;
  }

  & .ant-tag {
    font-size: 12px;
    line-height: 18px;
    width: 100%;
    text-align: center;
  }
`;

const Status = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;

  ${props => {
    switch (props.variant) {
      case 'LOAD':
        return css`
          color: ${props => props.color};
          background-color: transparent;
          border-width: max(0.3vw, 4px);
          border-color: ${props => props.color};
          border-style: solid;
        `;

      case 'MAINTENANCE':
        return css`
          background-color: #EFF2F5;
          color: rgba(0, 0, 0, 0.85);
        `;
      case 'UNLOAD':
      default:
        return css`
          background-color: ${props => props.color};
          color: #FFFFFF;
        `;
    }
  }}

  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  min-height: 100%;
`;

const DoorName = styled.span`
  margin-top: 16px;
`;

const DirectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 44px;
`;

const DirectionLabel = styled.span`
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  // color: #FFFFFF;
`;

const DetailsBox = styled.div`
  min-width: 160px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 14px 0 16px 0;
  padding-right: 16px;
  align-items: flex-end;
`;

const WorkersWrapper = styled(Space)`
  flex-wrap: wrap;
`;

const FavoriteIcon = ({ door, onClick }) => {
  return <Icon
    name="star"
    size={22}
    color={door.is_favorite === 1 ? '#FAAD14' : '#B9C2C9'}
    fill={door.is_favorite === 1 ? '#FAAD14' : ''}
    style={{
      marginTop: '4px',
    }}
    onClick={onClick}
  />
};

const TagIcon = ({ door }) => {
  const tagName = door.tag_name || '';
  if (!tagName) {
    return null;
  }

  return (
    <Tooltip title={tagName}>
      <Icon
        name="info"
        size={16}
        color="#42B0D5"
        style={{
          marginTop: '4px',
        }}
      />
    </Tooltip>
  );
};

const FinishedInfoBox = ({ door, toggleFavorite, dtFormat }) => {
  // const timeToDock = door.unload_duration + door.dock_start_iddle;
  const timeSinceStart = door.unload_duration;
  const startedAt = door.started_at ? formatDate(door.started_at * 1000, dtFormat, { tzOffset: door.timezone_offset }) : null;
  const dockedAt = door.docked_at ? formatDate(door.docked_at * 1000, dtFormat, { tzOffset: door.timezone_offset }) : null;
  const doorSpeed = door.relative_speed ? door.relative_speed.toLowerCase().replace('_', ' ') : null;
  const cartonPerHour = Math.round(door.avg_door_cph);
  const doorDir = door.direction || door.default_direction;

  return (
    <InfoBox>
      <Space>
        <FavoriteIcon door={door} onClick={toggleFavorite} />
        <Tooltip placement="top" title={'Process duration'} trigger="hover">
          <Text className="status">{formatDuration(timeSinceStart * 1000, false)}</Text>
        </Tooltip>
        {/* <StatusTag status={door.door_state} /> */}
        <TagIcon door={door} />
      </Space>
      <InfoLabel name="Docked" value={dockedAt} inline={true} />
      <InfoLabel name="Started" value={startedAt} inline={true} />
      <InfoLabel
        name="Speed"
        value={doorSpeed}
        style={{
          textTransform: 'capitalize',
          color: unloadSpeed.getColor(door.relative_speed),
        }}
        inline={true}
      />
      <InfoLabel name="CPH" value={cartonPerHour} inline={true} />
    </InfoBox>
  );
};

const InProgressInfoBox = ({ door, toggleFavorite, dtFormat }) => {
  // const timeToDock = door.unload_duration + door.dock_start_iddle;
  const timeSinceStart = door.unload_duration;
  const startedAt = door.started_at ? formatDate(door.started_at * 1000, dtFormat, { tzOffset: door.timezone_offset }) : null;
  const dockedAt = door.docked_at ? formatDate(door.docked_at * 1000, dtFormat, { tzOffset: door.timezone_offset }) : null;
  const doorSpeed = door.relative_speed ? door.relative_speed.toLowerCase().replace('_', ' ') : null;
  const cartonPerHour = Math.round(door.avg_door_cph);
  const doorDir = door.direction || door.default_direction;

  return (
    <InfoBox>
      <Space>
        <FavoriteIcon door={door} onClick={toggleFavorite} />
        <Tooltip placement="top" title={'Process duration'} trigger="hover">
          <Text className="status">{formatDuration(timeSinceStart * 1000, false)}</Text>
        </Tooltip>
        <TagIcon door={door} />
      </Space>
      <InfoLabel name="Docked" value={dockedAt} inline={true} />
      <InfoLabel name="Started" value={startedAt} inline={true} />
      <InfoLabel
        name="Speed"
        value={doorSpeed}
        style={{
          textTransform: 'capitalize',
          color: unloadSpeed.getColor(door.relative_speed),
        }}
        inline={true}
      />
      <InfoLabel name="CPH" value={cartonPerHour} inline={true} />
    </InfoBox>
  );
};

const StoppedInfoBox = ({ door, toggleFavorite, dtFormat }) => {
  // const timeToDock = door.unload_duration + door.dock_start_iddle;
  const timeSinceStart = door.unload_duration;
  const startedAt = door.started_at ? formatDate(door.started_at * 1000, dtFormat, { tzOffset: door.timezone_offset }) : null;
  const dockedAt = door.docked_at ? formatDate(door.docked_at * 1000, dtFormat, { tzOffset: door.timezone_offset }) : null;
  const doorSpeed = door.relative_speed ? door.relative_speed.toLowerCase().replace('_', ' ') : null;
  const cartonPerHour = Math.round(door.avg_door_cph);
  const doorDir = door.direction || door.default_direction;

  return (
    <InfoBox>
      <Space>
        <FavoriteIcon door={door} onClick={toggleFavorite} />
        <Tooltip placement="top" title={'Process duration'} trigger="hover">
          <Text className="status">{formatDuration(timeSinceStart * 1000, false)}</Text>
        </Tooltip>
        {/* <StatusTag status={door.door_state} /> */}
        <TagIcon door={door} />
      </Space>
      <InfoLabel name="Docked" value={dockedAt} inline={true} />
      <InfoLabel name="Started" value={startedAt} inline={true} />
      <InfoLabel
        name="Speed"
        value={doorSpeed}
        style={{
          textTransform: 'capitalize',
          color: unloadSpeed.getColor(door.relative_speed),
        }}
        inline={true}
      />
      <InfoLabel name="CPH" value={cartonPerHour} inline={true} />
    </InfoBox>
  );
};

const NotStartedInfoBox = ({ door, toggleFavorite, dtFormat }) => {
  // const timeToDock = door.unload_duration + door.dock_start_iddle;
  const dockedAt = door.docked_at ? formatDate(door.docked_at * 1000, dtFormat, { tzOffset: door.timezone_offset }) : null;
  const idleTime = formatDuration((door.dock_start_iddle || 0) * 1000, false);

  return (
    <InfoBox>
      <Space>
        <FavoriteIcon door={door} onClick={toggleFavorite} />
        <Text className="status">Not Started</Text>
        <TagIcon door={door} />
      </Space>
      <InfoLabel name="Docked" value={dockedAt} inline={true} />
      <InfoLabel name="Idle Time Before" value={idleTime} inline={true} />
    </InfoBox>
  );
};

const EmptyInfoBox = ({ door, toggleFavorite, dtFormat }) => {
  const emptyTime = (new Date() / 1000) - (door.docked_at + door.dock_start_iddle + door.unload_duration + door.dock_end_iddle);

  const lastUndocked = door.undocked_at ? formatDate(door.undocked_at * 1000, dtFormat, { tzOffset: door.timezone_offset }) : null;
  const emptyTimeFormated = door.undocked_at ? formatDuration(emptyTime * 1000, false) : null;

  return (
    <InfoBox>
      <Space>
        <FavoriteIcon door={door} onClick={toggleFavorite} />
        <Text className="status">Empty</Text>
        <TagIcon door={door} />
      </Space>
      <InfoLabel name="Last Undock" value={lastUndocked} inline={true} />
      <InfoLabel name="Empty Time" value={emptyTimeFormated} inline={true} />
    </InfoBox>
  );
};

const MaintenanceModeBox = ({ door, toggleFavorite }) => {
  return (
    <InfoBox>
      <Space>
        <FavoriteIcon door={door} onClick={toggleFavorite} />
        <Text className="status">Maintenance</Text>
        <TagIcon door={door} />
      </Space>
    </InfoBox>
  );
}

const CameraOfflineBox = ({ door, toggleFavorite }) => {
  return (
    <InfoBox>
      <Space>
        <FavoriteIcon door={door} onClick={toggleFavorite} />
        <Text className="status">Camera Offline</Text>
        <TagIcon door={door} />
      </Space>
    </InfoBox>
  );
}

const StorageInfoBox = ({ door, toggleFavorite }) => {
  return (
    <InfoBox>
      <Space>
        <FavoriteIcon door={door} onClick={toggleFavorite} />
        <Text className="status">{door.customer_name || '-'}</Text>
        <TagIcon door={door} />
      </Space>
    </InfoBox>
  );
};

const infoBoxes = {
  [doorState.FINISHED]: FinishedInfoBox,
  [doorState.UNLOADING]: InProgressInfoBox,
  [doorState.LOADING]: InProgressInfoBox,
  [doorState.STOPPED]: StoppedInfoBox,
  [doorState.NOT_STARTED]: NotStartedInfoBox,
  [doorState.EMPTY]: EmptyInfoBox,
};

const DoorCard = ({ door, toggleFavorite,hasCam,hasAnomaly, showIndividuals, onEmployeeAvatarClicked, ...rest }) => {
  const employees = useMemo(() => {
    let listLen = 4;
    const employees = { list: [], more: [], hasUnidentified: false };
    if (door) {
      employees.hasUnidentified = (door.unidentified_employees || 0) !== 0;
      if (employees.hasUnidentified) {
        listLen -= 1;
      }

      if (door.employees.length <= listLen) {
        employees.list = [...door.employees];
        employees.more = [];
      } else {
        employees.list = door.employees.slice(0, listLen - 1);
        employees.more = door.employees.slice(listLen - 1);
      }
    }
    return employees;
  }, [door]);

  const info = useMemo(() => {
    const info = {};
    if (door) {
      info.speedColor = unloadSpeed.getColor(door.relative_speed);
      info.statusColor = [doorState.UNLOADING, doorState.LOADING].includes(door.door_state) ? info.speedColor : doorState.getColor(door.door_state);
      info.unloadedPerc = Math.floor(door.unloaded_percent * 100);
      info.unloadedPercForcast = Math.floor(door.unloaded_percent_forecast * 100);
      info.dockedToday = door.docked_at ? door.docked_at - (Math.floor(new Date() / (24 * 1000 * 3600)) * (24 * 3600)) > 0 : true;
      info.direction = door.door_state !== doorState.EMPTY ? door.direction : null;
      info.maintenanceMode = door.maintenance_mode === 1;
      info.isStorageEquipment = door.container_code === "Equipment used for Pallets" || door.container_code === "Equipment used for Storage";
      const cname = (door.customer_name || '').trim().toLowerCase();
      info.isStorageCustomer = cname === 'storage' || cname === 'pallets';
    }
    return info;
  }, [door]);

  const {
    speedColor,
    statusColor,
    unloadedPerc,
    unloadedPercForcast,
    dockedToday,
    direction,
    maintenanceMode,
    isStorageEquipment,
    isStorageCustomer,
  } = info;
  // const dtFormat = dockedToday ? timeFormat : dateTimeFormat;
  const dtFormat = dateTimeFormat;

  const cameraOffline = !hasCam;
  const isStorageLike = isStorageEquipment || isStorageCustomer;
  const InfoBox = maintenanceMode
    ? MaintenanceModeBox
    : (cameraOffline
      ? CameraOfflineBox
      : (isStorageLike ? StorageInfoBox : infoBoxes[door.door_state]));
  if (!InfoBox) return null;

  return (
    <DoorCardBase {...rest}>
      <DoorCardContent>
        <Status style={{ flexBasis: '88px' }} color={statusColor} variant={(maintenanceMode || isStorageLike || cameraOffline) ? 'MAINTENANCE' : direction}>
          <DoorName>{door.name}</DoorName>
          <IconsContainer>
          {maintenanceMode ? (
            <Icon name='alert-triangle' />
          ) : (
            <>
              {cameraOffline && (
                <Icon name='video-off' />
              )}
              {hasAnomaly && <Icon name={cameraOffline ? 'video-off' : 'alert-triangle'} />}
              {(isStorageLike) && (
                <Icon name='archive' />
              )}
            </>
          )}
          </IconsContainer>
          <DirectionWrapper>
            {/* {direction === 'LOAD' && <Icon name="outbound" />} */}
            {/* {direction === 'UNLOAD' && <Icon name="inbound" />} */}
            {!(maintenanceMode || isStorageLike || cameraOffline) && <DirectionLabel>
              {direction === 'LOAD' ? 'Outbound' : null}
              {direction === 'UNLOAD' ? 'Inbound' : null}
            </DirectionLabel>}
          </DirectionWrapper>
        </Status>
        <InfoBox door={door} toggleFavorite={toggleFavorite} dtFormat={dtFormat} dockedToday={dockedToday} />
        {door.door_state !== doorState.EMPTY && door.door_state !== doorState.NOT_STARTED && !maintenanceMode && !isStorageLike && !cameraOffline ? (
          <DetailsBox>
            <ProcessBar
              completed={unloadedPerc}
              completedColor={direction === 'LOAD' ? '#232526' : speedColor}
              forcasted={unloadedPercForcast}
              forcastedColor={'#828C94'}
              showPrediction={direction === 'UNLOAD' && unloadedPercForcast !==0}
            />
            {showIndividuals && (
              <WorkersWrapper>
                {
                  employees.list.map((employee, i) => <Tooltip
                    key={i}
                    placement="top"
                    title={`${employee.full_name} (${Math.round(employee.avg_cph)} CPH)`}
                    trigger="hover"
                  >
                    <Avatar
                      onClick={(e) => {
                        e.stopPropagation();
                        if (typeof onEmployeeAvatarClicked === 'function') {
                          onEmployeeAvatarClicked(employee);
                        }
                      }}
                    >
                      {getInitials(employee.full_name)}
                    </Avatar>
                  </Tooltip>
                  )
                }
                {
                  employees.hasUnidentified ? (
                    <Tooltip
                      placement="top"
                      title={'Unidentified individual'}
                      trigger="hover"
                    >
                      <Avatar
                        icon={<Icon name="user" size={20} />}
                        style={{
                          backgroundColor: "#CF1322",
                        }}
                      />
                    </Tooltip>
                  ) : null
                }
                {
                  employees.more.length > 0 ? (
                    <Tooltip
                      placement="top"
                      title={employees.more.map(({ full_name, avg_cph }, i) => {
                        return <span key={i}>{`${full_name} (${Math.round(avg_cph)} CPH)`}<br /></span>
                      })}
                      trigger="hover"
                    >
                      <Avatar>+{employees.more.length}</Avatar>
                    </Tooltip>
                  ) : null
                }
              </WorkersWrapper>
            )}
          </DetailsBox>
        ) : null}
      </DoorCardContent>
    </DoorCardBase>
  );
};

export default DoorCard;
